// Carry Roll TrajectoryView — custom SVG trajectory + shape-distinct markers (7.1, 1.6).

import { useStore } from '../../store';
import { useResult } from '../../store/selectors';
import { convertDistance, mToFt } from '@carry-roll/shared-logic';
import type { TrajectoryPoint } from '@carry-roll/shared-types';

const VIEW_W = 400;
const VIEW_H = 200;
const PAD = 28;

export function TrajectoryView() {
  const result = useResult();
  const distanceUnit = useStore((s) => s.distanceUnit);
  const heightUnit = useStore((s) => s.heightUnit);

  const flight = result.flightPath;
  const roll = result.rollPath;

  const empty = flight.length < 2;
  const maxX = Math.max(result.totalM, roll.length ? (roll[roll.length - 1]?.x ?? result.totalM) : result.totalM, 1);
  const maxY = Math.max(result.apexM, 1);

  const dConv = (m: number) => convertDistance(m, distanceUnit);
  const hConv = (m: number) => (heightUnit === 'ft' ? mToFt(m) : m);
  const totalDisp = dConv(maxX);
  const apexDisp = hConv(maxY);

  const sx = (x: number) => PAD + (dConv(x) / totalDisp) * (VIEW_W - PAD * 2);
  const sy = (y: number) => VIEW_H - PAD - (hConv(y) / apexDisp) * (VIEW_H - PAD * 2);

  const flightPath = toPath(flight, sx, sy);
  const rollPath = roll.length > 1 ? toPath(roll, sx, sy) : '';

  const landing = flight[flight.length - 1];
  const resting = roll.length > 0 ? roll[roll.length - 1] : landing;
  const landingX = landing ? sx(landing.x) : 0;
  const restingX = resting ? sx(resting.x) : 0;

  return (
    <div
      className="panel"
      data-testid="trajectory-view"
      role="img"
      aria-label={
        empty
          ? 'Trajectory will appear once you enter your shot details.'
          : `Trajectory: carry ${Math.round(dConv(result.carryM))} ${distanceUnit}, total ${Math.round(dConv(result.totalM))} ${distanceUnit}, apex ${Math.round(hConv(result.apexM))} ${heightUnit}.`
      }
    >
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" style={{ display: 'block' }}>
        {/* baseline */}
        <line
          x1={PAD}
          y1={VIEW_H - PAD}
          x2={VIEW_W - PAD}
          y2={VIEW_H - PAD}
          stroke={empty ? 'var(--color-border)' : 'var(--color-text-muted)'}
          strokeWidth={1}
          strokeDasharray={empty ? '4 4' : undefined}
        />
        {!empty && (
          <>
            <path d={flightPath} fill="none" stroke="var(--color-carry)" strokeWidth={2.5} strokeLinecap="round" pathLength={1} className="traj-flight" />
            {rollPath && (
              <path d={rollPath} fill="none" stroke="var(--color-roll)" strokeWidth={2} strokeDasharray="4 3" strokeLinecap="round" />
            )}
            {/* landing marker ● (sky) */}
            <g data-testid="marker-landing" aria-label={`Landing point at ${Math.round(dConv(result.carryM))} ${distanceUnit}`}>
              <circle cx={landingX} cy={sy(0)} r={5} fill="var(--color-carry)" />
              <text x={landingX} y={sy(0) - 8} fill="var(--color-carry)" fontSize={9} textAnchor="middle">
                ● land
              </text>
            </g>
            {/* resting marker ▼ (emerald) */}
            <g data-testid="marker-resting" aria-label={`Resting point at ${Math.round(dConv(result.totalM))} ${distanceUnit}`}>
              <polygon
                points={`${restingX},${sy(0) - 6} ${restingX - 5},${sy(0) + 3} ${restingX + 5},${sy(0) + 3}`}
                fill="var(--color-total)"
              />
              <text x={restingX} y={sy(0) + 14} fill="var(--color-total)" fontSize={9} textAnchor="middle">
                ▼ rest
              </text>
            </g>
          </>
        )}
        {empty && (
          <>
            <circle cx={VIEW_W * 0.7} cy={sy(0)} r={5} fill="none" stroke="var(--color-border)" strokeWidth={1} />
            <text x={VIEW_W / 2} y={VIEW_H / 2} fill="var(--color-text-muted)" fontSize={11} textAnchor="middle">
              appears once you enter your shot
            </text>
          </>
        )}
        {/* axis labels */}
        <text x={VIEW_W / 2} y={VIEW_H - 4} fill="var(--color-text-muted)" fontSize={9} textAnchor="middle">
          distance ({distanceUnit})
        </text>
        <text x={6} y={VIEW_H / 2} fill="var(--color-text-muted)" fontSize={9} textAnchor="middle" transform={`rotate(-90 6 ${VIEW_H / 2})`}>
          height ({heightUnit})
        </text>
        {!empty && (
          <>
            {/* X-axis ticks: 0, half, max */}
            {[0, 0.5, 1].map((frac) => {
              const val = Math.round(frac * totalDisp);
              const xPx = PAD + frac * (VIEW_W - PAD * 2);
              const baseline = VIEW_H - PAD;
              return (
                <g key={`xtick-${frac}`}>
                  <line x1={xPx} y1={baseline} x2={xPx} y2={baseline + 4} stroke="var(--color-text-muted)" strokeWidth={1} />
                  <text x={xPx} y={baseline + 13} fill="var(--color-text-muted)" fontSize={8} textAnchor="middle">
                    {val}
                  </text>
                </g>
              );
            })}
            {/* Y-axis ticks: 0, half, max */}
            {[0, 0.5, 1].map((frac) => {
              const val = Math.round(frac * apexDisp);
              const yPx = VIEW_H - PAD - frac * (VIEW_H - PAD * 2);
              return (
                <g key={`ytick-${frac}`}>
                  <line x1={PAD - 4} y1={yPx} x2={PAD} y2={yPx} stroke="var(--color-text-muted)" strokeWidth={1} />
                  <text x={PAD - 6} y={yPx + 3} fill="var(--color-text-muted)" fontSize={8} textAnchor="end">
                    {val}
                  </text>
                </g>
              );
            })}
          </>
        )}
      </svg>
      {!empty && (
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Solid line is flight, dashed line is roll. ● marks the landing point; ▼ marks where the ball comes to rest.
        </p>
      )}
    </div>
  );
}

function toPath(points: TrajectoryPoint[], sx: (x: number) => number, sy: (y: number) => number): string {
  if (points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.x).toFixed(2)} ${sy(p.y).toFixed(2)}`)
    .join(' ');
}
